import { useTranslation } from '@kgc/i18n';
import { Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@kgc/ui';
import { useState } from 'react';

type Frequency = 'daily' | 'weekdays' | 'weekly' | 'monthly' | 'custom';

const DAY_OPTIONS = [
  { value: '1', labelKey: 'integration.flow.schedule.monday' },
  { value: '2', labelKey: 'integration.flow.schedule.tuesday' },
  { value: '3', labelKey: 'integration.flow.schedule.wednesday' },
  { value: '4', labelKey: 'integration.flow.schedule.thursday' },
  { value: '5', labelKey: 'integration.flow.schedule.friday' },
  { value: '6', labelKey: 'integration.flow.schedule.saturday' },
  { value: '0', labelKey: 'integration.flow.schedule.sunday' },
];

function buildCronExpression(frequency: Frequency, time: string, day: string): string {
  const [hour, minute] = time.split(':').map(Number);
  const h = hour ?? 0;
  const m = minute ?? 0;

  switch (frequency) {
    case 'daily':
      return `${m} ${h} * * *`;
    case 'weekdays':
      return `${m} ${h} * * 1-5`;
    case 'weekly':
      return `${m} ${h} * * ${day}`;
    case 'monthly':
      return `${m} ${h} ${day} * *`;
    case 'custom':
      return '';
  }
}

interface CronPickerProps {
  value: string;
  onChange: (cron: string) => void;
}

export function CronPicker({ value, onChange }: CronPickerProps) {
  const { t } = useTranslation();
  const [frequency, setFrequency] = useState<Frequency>('daily');
  const [time, setTime] = useState('18:00');
  const [day, setDay] = useState('1');

  function handleFrequencyChange(freq: Frequency) {
    setFrequency(freq);
    if (freq === 'custom') return;
    onChange(buildCronExpression(freq, time, day));
  }

  function handleTimeChange(newTime: string) {
    setTime(newTime);
    if (frequency === 'custom') return;
    onChange(buildCronExpression(frequency, newTime, day));
  }

  function handleDayChange(newDay: string) {
    setDay(newDay);
    if (frequency === 'custom') return;
    onChange(buildCronExpression(frequency, time, newDay));
  }

  return (
    <div className="space-y-3">
      <Select value={frequency} onValueChange={v => handleFrequencyChange(v as Frequency)}>
        <SelectTrigger data-testid="flow-cron-frequency">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="daily">{t('integration.flow.schedule.daily')}</SelectItem>
          <SelectItem value="weekdays">{t('integration.flow.schedule.weekdays')}</SelectItem>
          <SelectItem value="weekly">{t('integration.flow.schedule.weekly')}</SelectItem>
          <SelectItem value="monthly">{t('integration.flow.schedule.monthly')}</SelectItem>
          <SelectItem value="custom">{t('integration.flow.schedule.custom')}</SelectItem>
        </SelectContent>
      </Select>

      {frequency !== 'custom' && (
        <Input
          type="time"
          value={time}
          onChange={e => handleTimeChange(e.target.value)}
          data-testid="flow-cron-time"
        />
      )}

      {frequency === 'weekly' && (
        <Select value={day} onValueChange={handleDayChange}>
          <SelectTrigger data-testid="flow-cron-day">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DAY_OPTIONS.map(d => (
              <SelectItem key={d.value} value={d.value}>
                {t(d.labelKey)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {frequency === 'monthly' && (
        <Input
          type="number"
          min={1}
          max={28}
          value={day}
          onChange={e => handleDayChange(e.target.value)}
          placeholder={t('integration.flow.schedule.dayOfMonth')}
          data-testid="flow-cron-day-of-month"
        />
      )}

      {frequency === 'custom' && (
        <Input
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder="0 18 * * *"
          data-testid="flow-cron-custom"
        />
      )}
    </div>
  );
}
